import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Calendar } from 'lucide-react';
import AdSpace from '../components/AdSpace';
import { ARTICLES } from './BlogIndex';
import { useSetMeta } from '../hooks/useSetMeta';

interface BlogPostProps {
  slug: string;
}

const BlogPost: React.FC<BlogPostProps> = ({ slug }) => {
  const [data, setData] = useState<{ title: string; description: string; html: string } | null>(null);
  const [error, setError] = useState(false);

  const articleMeta = ARTICLES.find(a => a.slug === slug);

  // Set dynamic meta tags based on article data
  useSetMeta({
    title: articleMeta ? `${articleMeta.title} | ZodiAge Blog` : 'Blog Post | ZodiAge',
    description: articleMeta ? articleMeta.excerpt.substring(0, 160) : 'Read our latest astrology and zodiac blog posts.',
    ogImage: 'https://zodiage.com/logo.png'
  });

  useEffect(() => {
    // Dynamic import for Vite
    import(`../src/content/blog/${slug}.json`)
      .then(module => {
        setData(module.default);
      })
      .catch(err => {
        console.error("Failed to load blog post", err);
        setError(true);
      });
  }, [slug]);

  if (error || (!articleMeta && !data)) {
      return (
          <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4 animate-in fade-in">
              <h1 className="text-3xl font-display font-bold text-white mb-4">Post Not Found</h1>
              <p className="text-gray-400 mb-8">The cosmic archives do not contain this record.</p>
              <button 
                  onClick={() => { window.history.pushState({}, '', '/blog'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                  className="bg-purple-600 text-white font-bold py-3 px-8 rounded-full shadow-lg"
              >
                  Return to Journal
              </button>
          </div>
      );
  }

  if (!data) {
      return (
          <div className="flex items-center justify-center min-h-[50vh]">
              <Loader2 size={40} className="text-purple-500 animate-spin" />
          </div>
      );
  }

  return (
    <div className="max-w-3xl mx-auto py-12 px-4 animate-in fade-in duration-700">
        <button 
            onClick={() => { window.history.pushState({}, '', '/blog'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            className="group flex items-center text-sm font-bold text-gray-400 hover:text-white transition-colors mb-12"
        >
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Journal
        </button>

        <header className="mb-12">
            {articleMeta && (
                <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-wider mb-4">
                    <span className="text-secondary bg-secondary/10 px-3 py-1 rounded-full border border-secondary/20">
                        {articleMeta.category}
                    </span>
                    <span className="text-gray-500 flex items-center gap-1">
                        <Calendar size={12} /> {articleMeta.date}
                    </span>
                </div>
            )}
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
                {data.title}
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed italic border-l-4 border-gray-800 pl-4">
                {data.description}
            </p>
        </header>

        <AdSpace type="in-article" slot="7716101506" className="my-8" />

        <div className="bg-card/30 border border-gray-800 rounded-2xl p-6 md:p-10 shadow-2xl">
            <article 
                className="seo-content prose prose-invert max-w-none 
                    prose-headings:font-display prose-headings:font-bold prose-headings:text-white
                    prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-gray-800 prose-h2:pb-2
                    prose-h3:text-2xl prose-h3:text-purple-300 prose-h3:mt-8
                    prose-p:text-gray-300 prose-p:leading-loose prose-p:mb-6
                    prose-a:text-pink-400 prose-a:no-underline hover:prose-a:text-pink-300
                    prose-strong:text-white prose-strong:font-bold
                    prose-ul:list-disc prose-ul:text-gray-300 prose-ul:pl-6
                    prose-li:mb-2
                    prose-blockquote:border-l-4 prose-blockquote:border-purple-500 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-400 prose-blockquote:bg-purple-900/10 prose-blockquote:py-2 prose-blockquote:rounded-r-lg
                "
                dangerouslySetInnerHTML={{ __html: data.html }}
            />
        </div>

        <AdSpace type="multiplex" slot="9466105368" className="mt-16" />

        <div className="mt-16 text-center">
            <p className="text-gray-500 mb-6 italic">Thanks for reading. Share this wisdom if it resonated with you.</p>
            <button 
                onClick={() => { window.history.pushState({}, '', '/blog'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-full border border-gray-700 transition-colors"
            >
                Read More Articles
            </button>
        </div>
    </div>
  );
};

export default BlogPost;
